import { Lock, Mail } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { useBillingStateQuery } from "@/frontend/queries/dashboard/billing";
import { PLAN_CODE, type Feature, type PlanCode } from "@/server/types/billing";
import type { FeatureLockedProps } from "@/frontend/types/billing";
import { FEATURE_LABEL, PLAN_LABEL, REQUIRED_PLAN } from "@/frontend/constants/plans";
import { supportMailto } from "@/frontend/utils/support";

/**
 * Upsell screen shown in place of a gated settings section when the caller's
 * plan doesn't include `feature`. Keeps the section discoverable — users see
 * what they'd get with an upgrade — while denying access.
 */
export function FeatureLocked({ feature, title, description }: FeatureLockedProps) {
  const { data: state } = useBillingStateQuery();
  const currentPlan: PlanCode = state?.plan_code ?? PLAN_CODE.FREE;
  const required = REQUIRED_PLAN[feature];
  const heading = title ?? FEATURE_LABEL[feature];

  return (
    <div className="max-w-2xl">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold">{heading}</h2>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-dashed p-6 flex flex-col gap-4 bg-muted/20">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-muted p-2">
            <Lock className="size-5 text-muted-foreground" />
          </div>
          <div>
            <div className="font-medium">This feature isn't part of your plan</div>
            <div className="text-sm text-muted-foreground">
              You're on <span className="font-medium">{PLAN_LABEL[currentPlan]}</span>. Contact
              support to upgrade to <span className="font-medium">{required.label}</span> and
              unlock {FEATURE_LABEL[feature]}.
            </div>
          </div>
        </div>

        <div>
          <Button asChild>
            <a href={supportMailto(`Unlock ${FEATURE_LABEL[feature]} (${required.label})`)}>
              <Mail className="size-4 mr-2" />
              Contact support to upgrade
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Wrap a section component with a plan gate. When the caller's plan doesn't
 * include `feature`, renders the upsell instead of the wrapped section.
 */
export function withFeatureGate<P extends object>(
  Component: React.ComponentType<P>,
  feature: Feature,
  meta?: { title?: string; description?: string },
): React.ComponentType<P> {
  return function GatedSection(props: P) {
    return (
      <FeatureGateInner feature={feature} meta={meta}>
        <Component {...props} />
      </FeatureGateInner>
    );
  };
}

/** Renders `children` when the caller's plan includes `feature`; otherwise renders the upsell. Fails closed on query error. */
function FeatureGateInner({
  feature,
  meta,
  children,
}: {
  feature: Feature;
  meta?: { title?: string; description?: string };
  children: React.ReactNode;
}) {
  const { data: state, isLoading, isError } = useBillingStateQuery();
  if (isLoading) return <>{children}</>;
  if (isError || !state) return <FeatureLocked feature={feature} title={meta?.title} description={meta?.description} />;
  const enabled = state.features?.[feature] === true;
  if (!enabled) return <FeatureLocked feature={feature} title={meta?.title} description={meta?.description} />;
  return <>{children}</>;
}
